import React, { useEffect, useRef, useState } from "react";
import { LButton } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong, faCodeCompare, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import styles from "./GabSyncManagement.module.css";
import { GabCompareDefaults } from "./types";

interface Props {
  productName: string;
  busy: boolean;
  // Why the last compare failed, rendered inline so it stays put until the next attempt.
  error?: string | null;
  defaults: GabCompareDefaults | null;
  onCompare: (sourceNameSuffix: string, targetNameSuffix: string, newOnly: boolean) => void;
}

// Illustrative name used only for the live preview of the suffix round-trip.
const SAMPLE = "ACME BROKER";

// Start screen for a single GAB product: the source is the environment-configured Prod backup, the
// target is the product's own database. The operator confirms the name-suffix round-trip (PROD is
// truth — strip the source's suffix, stamp this environment's) before running the compare. Nothing
// is written from here.
function SyncStartPanel({ productName, busy, error, defaults, onCompare }: Props) {
  const [sourceSuffix, setSourceSuffix] = useState("");
  const [targetSuffix, setTargetSuffix] = useState("");
  const [newOnly, setNewOnly] = useState(false);

  // @loffa LButton freezes its onClick at mount (see ApplyPanel), so a closure over these state values
  // would compare with the mount-time values. Read the live values from refs on click instead.
  const sourceRef = useRef("");
  const targetRef = useRef("");
  const newOnlyRef = useRef(false);
  sourceRef.current = sourceSuffix;
  targetRef.current = targetSuffix;
  newOnlyRef.current = newOnly;

  // Pre-fill from the environment's configured defaults once they load; the operator can override.
  useEffect(() => {
    if (defaults) {
      setSourceSuffix(defaults.sourceNameSuffix ?? "");
      setTargetSuffix(defaults.targetNameSuffix ?? "");
    }
  }, [defaults]);

  const sourceLabel = defaults?.sourceLabel || "Prod Backup";

  // Live preview: a sample name carrying the source suffix, normalised then re-tagged for this env.
  const sampleIn = SAMPLE + (sourceSuffix || "");
  const stripped =
    sourceSuffix && sampleIn.toLowerCase().endsWith(sourceSuffix.toLowerCase())
      ? sampleIn.slice(0, sampleIn.length - sourceSuffix.length)
      : sampleIn;
  const sampleOut = stripped + (targetSuffix || "");

  return (
    <div className={styles.connectionPanel}>
      <div className={styles.panelIntro}>
        <p className={styles.introText}>
          Compare <strong>{productName}</strong> against the configured production backup. Every
          difference is reviewed before anything is written — nothing is applied from this step.
        </p>
      </div>

      <label className={styles.addOnlyOption}>
        <input
          type="checkbox"
          checked={newOnly}
          onChange={(e) => setNewOnly(e.target.checked)}
          disabled={busy}
        />
        <span>
          <strong>Adds only</strong> — only add records whose <code>comp_dtc</code> / <code>EB_MPID</code>{" "}
          isn't in this environment yet. Skips every row that already exists, so changes to existing rows
          are ignored (and the compare runs faster — it doesn't diff what you already have).
        </span>
      </label>

      {error && (
        <div className={styles.applyError} role="alert">
          <FontAwesomeIcon icon={faTriangleExclamation} className={styles.applyErrorIcon} />
          <div>
            <div className={styles.applyErrorTitle}>Compare did not run — nothing was written</div>
            <div className={styles.applyErrorBody}>{error}</div>
          </div>
        </div>
      )}

      <div className={styles.connectionList}>
        <div className={styles.connectionCard}>
          <div className={styles.connectionInfo}>
            <div className={styles.connectionName}>{productName}</div>
            <div className={styles.connectionRoute}>
              <span className={styles.sourceLabel}>{sourceLabel}</span>
              <FontAwesomeIcon icon={faArrowRightLong} className={styles.routeArrow} />
              <span className={styles.targetLabel}>This environment</span>
            </div>
          </div>
          <LButton
            themeColor="primary"
            disabled={busy}
            onClick={() => onCompare(sourceRef.current, targetRef.current, newOnlyRef.current)}
          >
            {busy ? (
              "Comparing…"
            ) : (
              <>
                <FontAwesomeIcon icon={faCodeCompare} /> Run Compare
              </>
            )}
          </LButton>
        </div>
      </div>

      <div className={styles.tagPanel}>
        <div className={styles.tagTitle}>Name tagging</div>
        <p className={styles.tagIntro}>
          PROD is the source of truth. Names are normalised to their canonical value, then tagged for
          this environment — so a name that differs only by its environment suffix isn't flagged as a
          change. Leave <em>source suffix</em> blank when copying from real Prod; set it when the source
          already carries a tag (e.g. a Test copy).
        </p>

        <div className={styles.tagFields}>
          <label className={styles.tagField}>
            <span className={styles.tagLabel}>Source names end with</span>
            <input
              className={styles.tagInput}
              placeholder="(none)"
              value={sourceSuffix}
              onChange={(e) => setSourceSuffix(e.target.value)}
              disabled={busy}
            />
            <span className={styles.tagHint}>stripped from incoming names</span>
          </label>
          <label className={styles.tagField}>
            <span className={styles.tagLabel}>Tag names in this environment as</span>
            <input
              className={styles.tagInput}
              placeholder="(none)"
              value={targetSuffix}
              onChange={(e) => setTargetSuffix(e.target.value)}
              disabled={busy}
            />
            <span className={styles.tagHint}>appended to every synced name</span>
          </label>
        </div>

        <div className={styles.tagPreview}>
          <span className={styles.tagPreviewLabel}>Preview</span>
          <code className={styles.tagPreviewIn}>{sampleIn}</code>
          <FontAwesomeIcon icon={faArrowRightLong} className={styles.routeArrow} />
          <code className={styles.tagPreviewOut}>{sampleOut}</code>
        </div>
      </div>
    </div>
  );
}

export { SyncStartPanel };
